import React, { FC, useState, useEffect } from "react";
import {
  ClientValue,
  CreateProposal,
  MatterTitleScreen,
  ClientFilterData,
  OnClientSave,
  MatterFormMode,
  MatterTitleScreenTabKey,
  AccruedFee,
  MatterDetailsScreen,
  CreateProposalScreens,
  MatterFilter,
  ExternalMatter,
  BaseAccruedFee,
  TitleInputState,
  DeprecatedRatesScreen,
  RelevantMatter,
} from "@design-system";
import { FormInstance, Layout } from "antd";
import { Permission, usePermissionCheck } from "../hooks";
import { useQuery, useLazyQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { CREATE_MATTER, CREATE_CLIENT } from "../graphql/mutation";
import { FIND_PARENT_MATTER_QUERY } from "~/containers/parent-matter/parent-matter-query";
import {
  ORGANISATIONAL_UNITS_QUERY,
  FILTERED_CLIENTS_QUERY,
  BEDROCK_CHECK_MATTER_TITLE_IS_DESCRIPTIVE,
} from "../graphql/query";
import { useMutation, useSettings } from "../hooks";
import {
  CreateClientMutation,
  CreateClientMutationVariables,
  FindFilteredClientsQuery,
  FindFilteredClientsQueryVariables,
  FindOrganisationalUnitsQuery,
  RateReplacement,
} from "~/gql/graphql";
import { ExternalMatterStatus } from "../graphql/types";
import { useCreateMultiDisc } from "~/graphql/mutation/create-parent-matter";
import { useExternalMattersQueryLazy } from "../hooks/external-matters";
import { useFilteredClientsLazy } from "../hooks/clients";
import { FeeEarnerCascaderContainer } from "./fee-earner-cascader-container";
import { useRelevantMatterSearch, useRelevantExternalMatterSearch } from "../hooks/matters";
import styles from "./create-proposal-container.css";

const {
  layout: layoutClassName,
  content: contentClassName,
  container: containerClassName,
} = styles;

const INITIAL_MATTER_FILTER: MatterFilter = {
  title: null,
  client: null,
  orgUnit: null,
  status: null,
  updatedAt: null,
  orderBy: null,
  createdBy: null,
  currencyId: null,
  feeEarner: null,
  someTimeEntries: true,
  linked: null,
};

export const CreateProposalContainer: FC<{
  parentMatterId?: number;
  client?: ClientValue;
}> = ({ parentMatterId, client }) => {
  const [matterTitle, setMatterTitle] = useState("");
  const [additionalSearchText, setAdditionalSearchText] = useState("");
  const [selectedMatter, setSelectedMatter] = useState<RelevantMatter | null>(null);
  const [selectedExternalMatter, setSelectedExternalMatter] = useState<ExternalMatter | null>(null);
  const [selectedAccruedFees, setSelectedAccruedFees] = useState<AccruedFee | null>(null);
  const [selectedExternalAccruedFees, setSelectedExternalAccruedFees] =
    useState<BaseAccruedFee | null>(null);
  const [activeTab, setActiveTab] = useState<MatterTitleScreenTabKey>(
    MatterTitleScreenTabKey.TemplateMatters
  );
  const [isMatterTitleDescriptive, setIsMatterTitleDescriptive] = useState<boolean | undefined>(
    undefined
  );

  const [matterFilter, setMatterFilter] = useState<MatterFilter>(INITIAL_MATTER_FILTER);

  const { settings } = useSettings();
  const defaultCurrency = settings?.currency;
  const defaultCurrencyId = defaultCurrency?.id;
  useEffect(() => {
    if (defaultCurrencyId) {
      setMatterFilter({
        ...matterFilter,
        currencyId: [defaultCurrencyId],
      });
    }
  }, [defaultCurrencyId]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { createMultiDiscMatter } = useCreateMultiDisc();

  const [createMatter] = useMutation(CREATE_MATTER, {
    refetchQueries: (data) => {
      if (data.data?.createMatter.parent?.id) {
        return [
          {
            query: FIND_PARENT_MATTER_QUERY,
            variables: { id: data.data.createMatter.parent?.id },
          },
        ];
      }
      return [];
    },
    onCompleted: (data) => {
      if (data.createMatter.parent?.id && !pathname.includes("parent")) {
        return navigate(`/parent/${data.createMatter.parent?.id}`, { replace: true });
      }
      return navigate(`/matter-editor/${data.createMatter.id}`, { replace: true });
    },
    update: (cache, createdMatter) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredMatters",
      });

      if (createdMatter.data?.createMatter.externalMatterId) {
        cache.modify({
          id: `ExternalMatter:${createdMatter.data?.createMatter.externalMatterId}`,
          fields: {
            status() {
              return (
                createdMatter.data?.createMatter.externalMatter?.status ??
                ExternalMatterStatus.Unassigned
              );
            },
            matter() {
              return {
                id: createdMatter.data?.createMatter.id,
              };
            },
          },
        });
      }
    },
  });

  const [selectedClient, setSelectedClient] = useState<ClientValue | undefined | null>(client);

  useEffect(() => {
    setSelectedClient(client);
  }, [client]);

  const clientFilterData = useFilteredClientsLazy(10, true);

  const [createClient] = useMutation<CreateClientMutation, CreateClientMutationVariables>(
    CREATE_CLIENT,
    {
      update: (cache, mutationResult) => {
        const newClient = mutationResult.data?.createClient;
        if (!newClient) {
          return;
        }

        const data = cache.readQuery<FindFilteredClientsQuery, FindFilteredClientsQueryVariables>({
          query: FILTERED_CLIENTS_QUERY,
          variables: clientFilterData?.queryVars,
        });

        cache.writeQuery<FindFilteredClientsQuery, FindFilteredClientsQueryVariables>({
          query: FILTERED_CLIENTS_QUERY,
          variables: {
            ...clientFilterData?.queryVars,
          },
          data: {
            filteredClients: {
              clients: [...(data?.filteredClients?.clients || []), newClient],
              total: (data?.filteredClients?.total ?? 0) + 1,
              __typename: "ClientResult",
            },
          },
        });
        if (newClient?.name) {
          setSelectedClient({
            ...newClient,
          });
          clientFilterData.search(newClient?.name);
        }
      },
    }
  );

  const { data: organisationalUnitData } = useQuery<FindOrganisationalUnitsQuery>(
    ORGANISATIONAL_UNITS_QUERY
  );

  const {
    templatesData,
    templatesLoading,
    historicalData,
    historicalLoading,
    triggerSearch: triggerMatterSearch,
  } = useRelevantMatterSearch(matterTitle, matterFilter, {
    additionalSearchText: additionalSearchText?.trim() || undefined,
    autoRetry: true,
    fetchTemplates: true,
  });

  const {
    externalMattersData,
    externalMattersLoading,
    triggerSearch: triggerExternalMatterSearch,
  } = useRelevantExternalMatterSearch(matterTitle, matterFilter, {
    additionalSearchText: additionalSearchText?.trim() || undefined,
    autoRetry: true,
    unlinkedOnly: true,
  });

  const [checkMatterTitleIsDescriptive, { loading: checkMatterTitleIsDescriptiveLoading }] =
    useLazyQuery(BEDROCK_CHECK_MATTER_TITLE_IS_DESCRIPTIVE);

  useEffect(() => {
    if (matterTitle.trim()) {
      checkMatterTitleIsDescriptive({ variables: { matterTitle: matterTitle } }).then(
        (data: any) => {
          setIsMatterTitleDescriptive(data.data?.bedrockCheckMatterTitleIsDescriptive);
        }
      );
    }
  }, [matterTitle, checkMatterTitleIsDescriptive]);

  const handleFilterChange = (newFilters: MatterFilter) => {
    setMatterFilter(newFilters);
  };

  const externalMatterData = useExternalMattersQueryLazy();

  const canCreateMatter = usePermissionCheck(Permission.CREATE_MATTER);
  if (!canCreateMatter) {
    return null;
  }

  const handleSelectedMatterChange = (matter: RelevantMatter | null) => {
    setSelectedMatter(matter);
    setSelectedAccruedFees(
      historicalData?.findRelevantMatters?.accruedFees?.find(
        (fee) => fee.matterId === matter?.id
      ) || null
    );
    setSelectedExternalMatter(null);
    setSelectedExternalAccruedFees(null);
  };

  const handleSelectedExternalMatterChange = (matter: ExternalMatter | null) => {
    setSelectedExternalMatter(matter);
    setSelectedExternalAccruedFees(
      externalMattersData?.findRelevantExternalMatters?.accruedFees?.find(
        (fee) => fee.externalMatterId === matter?.id
      ) || null
    );
    setSelectedMatter(null);
    setSelectedAccruedFees(null);
  };

  const clearRelevantMatters = () => {
    setMatterTitle("");
  };

  const matterTitleScreen = (
    form: FormInstance,
    options: {
      uiState: TitleInputState;
      setUiState: (state: TitleInputState) => void;
    }
  ) => (
    <MatterTitleScreen
      form={form}
      loading={templatesLoading || historicalLoading || externalMattersLoading}
      templateMattersLoading={templatesLoading || false}
      historicalMattersLoading={historicalLoading}
      matterTitle={matterTitle}
      additionalSearchText={additionalSearchText}
      setAdditionalSearchText={setAdditionalSearchText}
      templateMatters={templatesData}
      historicalMatters={historicalData}
      historicalComparisonLoading={externalMattersLoading}
      externalMatters={externalMattersData}
      selectedMatter={selectedMatter}
      setSelectedMatter={handleSelectedMatterChange}
      selectedAccruedFees={selectedAccruedFees}
      selectedExternalMatter={selectedExternalMatter}
      setSelectedExternalMatter={handleSelectedExternalMatterChange}
      selectedExternalAccruedFees={selectedExternalAccruedFees}
      handleMatterTitleChange={setMatterTitle}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      isMatterTitleDescriptive={
        checkMatterTitleIsDescriptiveLoading || !matterTitle ? undefined : isMatterTitleDescriptive
      }
      matterFilter={matterFilter}
      handleFilterChange={handleFilterChange}
      uiState={options.uiState}
      setUiState={options.setUiState}
    />
  );

  const matterDetailsScreen = (
    form: FormInstance,
    setScreen: (screen: CreateProposalScreens) => void
  ) => {
    return (
      <MatterDetailsScreen
        setScreen={setScreen}
        selectedMatter={selectedMatter}
        setSelectedMatter={setSelectedMatter}
        client={selectedClient}
        setCurrentClient={(client) => {
          setSelectedClient(client);
        }}
        template={"matter"}
        parentMatterId={parentMatterId}
        form={form}
        externalMatterData={externalMatterData}
        pmsName={settings?.organisationPmsName || ""}
        practiceAreas={organisationalUnitData?.organisationalUnits || []}
        onClientSave={
          ((values: any) => {
            createClient({ variables: { data: values as any } });
          }) as OnClientSave
        }
        clientFilterData={clientFilterData as ClientFilterData}
        feeEarnersCascaderRender={() => <FeeEarnerCascaderContainer showSearch />}
        mode={MatterFormMode.Create}
        selectedCurrencyId={matterFilter.currencyId?.[0] ?? undefined}
      />
    );
  };

  // Check if the selected historical matter has deprecated rates
  const hasDeprecatedRates =
    selectedMatter &&
    !selectedMatter.template &&
    selectedMatter.teamMemberships &&
    selectedMatter.teamMemberships.some((tm) => tm.rate.deprecated || tm.feeEarner?.deprecated);

  const deprecatedRatesScreen = (
    form: FormInstance,
    setScreen: (screen: CreateProposalScreens) => void,
    setRateReplacements: (replacements: RateReplacement[]) => void,
    setAllRatesReplaced: (allReplaced: boolean) => void
  ) => {
    if (!selectedMatter || !selectedMatter.teamMemberships) {
      return null;
    }

    const deprecatedRates = selectedMatter.teamMemberships.filter(
      (tm) => tm.rate.deprecated || tm.feeEarner?.deprecated
    );

    return (
      <DeprecatedRatesScreen
        form={form}
        setScreen={setScreen}
        setRateReplacements={setRateReplacements}
        setAllRatesReplaced={setAllRatesReplaced}
        deprecatedRates={deprecatedRates}
        matterId={selectedMatter.id}
        clientId={selectedMatter.client?.id}
        organisationalUnitId={selectedMatter.organisationalUnit?.id}
        currency={selectedMatter.currency || defaultCurrency || { code: "", symbol: "" }}
        pricingType={selectedMatter.pricingType}
      />
    );
  };

  return (
    <div className={containerClassName}>
      <Layout className={layoutClassName}>
        <Layout.Content className={contentClassName}>
          <CreateProposal
            parentMatterId={parentMatterId}
            onCreate={({
              templateId,
              historicalMatterId,
              copyScope,
              copyResourcing,
              copyPricing,
              practiceAreaId,
              parentPhaseId,
              rateReplacements,
              ...values
            }: any) => {
              createMatter({
                variables: {
                  createFromOptions: {
                    templateId: templateId || null,
                    historicalMatterId: historicalMatterId || null,
                    copyMatterDetails: {
                      copyScope: copyScope || false,
                      copyResourcing: copyResourcing || false,
                      copyPricing: copyPricing || false,
                    },
                    rateReplacements: rateReplacements || [],
                  },
                  parentId: parentMatterId || null,
                  parentPhaseId: parentPhaseId || null,
                  data: {
                    ...(practiceAreaId && {
                      organisationalUnit: { connect: { id: practiceAreaId } },
                    }),
                    ...(values?.clientId && {
                      client: { connect: { id: values.clientId } },
                    }),
                    ...(values.externalMatterId && {
                      externalMatter: { connect: { id: values.externalMatterId } },
                    }),
                    ...(values?.leadFeeEarnerId && {
                      feeEarner: { connect: { id: values.leadFeeEarnerId } },
                    }),
                    title: values.title,
                    status: values.status,
                    pricingType: values.pricingType,
                    pricingLevel: values.pricingLevel,
                    pricingTarget: values.pricingTarget,
                    ...(values.matterCurrencyId && {
                      currency: {
                        connect: {
                          id: values.matterCurrencyId,
                        },
                      },
                    }),
                    ...(values.officeLocationId && {
                      officeLocation: {
                        connect: {
                          id: values.officeLocationId,
                        },
                      },
                    }),
                    confidential: values.confidential,
                  },
                },
              });
            }}
            createMultiDiscMatter={() => {}}
            selectedMatter={selectedMatter}
            setSelectedMatter={setSelectedMatter}
            selectedExternalMatter={selectedExternalMatter}
            setSelectedExternalMatter={setSelectedExternalMatter}
            matterTitleScreen={matterTitleScreen}
            matterDetailsScreen={matterDetailsScreen}
            deprecatedRatesScreen={deprecatedRatesScreen}
            hasDeprecatedRates={!!hasDeprecatedRates}
            onCancel={() => {
              Promise.resolve().then(() => {
                setAdditionalSearchText("");
                setMatterFilter(INITIAL_MATTER_FILTER);
                clearRelevantMatters();
              });
            }}
            setActiveTab={setActiveTab}
          />
        </Layout.Content>
      </Layout>
    </div>
  );
};
